/**
 * The video quality ladder — one list for every app.
 *
 * These presets started life in the dashboard's device editor, and the fleet console then grew its
 * own three-step ladders with different numbers, so "Medium" meant 640×480 on one screen and
 * 1280×720 on another and neither recognised the other's saves as a named profile. This module is
 * the single source: the dashboard's original values, verbatim. The fleet defaults' stated
 * profiles (streaming Lowest, recording Medium) are named here too, so the C# defaults in
 * FleetDefaultsBase and every UI describing them point at the same rungs.
 */

export type VideoQualityProfile = {
    key: string;
    /** The rung's name alone — each app formats its own label around it. */
    name: string;
    resolutionX: number;
    resolutionY: number;
    fps: number;
    bitrateBps: number;
};

export const VIDEO_QUALITY_PROFILES: VideoQualityProfile[] = [
    { key: "lowest", name: "Lowest", resolutionX: 640, resolutionY: 480, fps: 5, bitrateBps: 50_000 },
    { key: "low", name: "Low", resolutionX: 854, resolutionY: 480, fps: 10, bitrateBps: 250_000 },
    { key: "medium-low", name: "Medium-Low", resolutionX: 1280, resolutionY: 720, fps: 10, bitrateBps: 500_000 },
    { key: "medium", name: "Medium", resolutionX: 1280, resolutionY: 720, fps: 15, bitrateBps: 1_000_000 },
    { key: "medium-high", name: "Medium-High", resolutionX: 1920, resolutionY: 1080, fps: 15, bitrateBps: 2_000_000 },
    { key: "high", name: "High", resolutionX: 1920, resolutionY: 1080, fps: 24, bitrateBps: 4_000_000 },
    { key: "max", name: "Max", resolutionX: 1920, resolutionY: 1080, fps: 30, bitrateBps: 6_000_000 },
];

/** What a new fleet runs until a director says otherwise — mirrored by FleetDefaultsBase in C#. */
export const DEFAULT_STREAM_PROFILE_KEY = "lowest";
export const DEFAULT_RECORDING_PROFILE_KEY = "medium";

export const videoProfileByKey = (key: string): VideoQualityProfile | undefined =>
    VIDEO_QUALITY_PROFILES.find((p) => p.key === key);
